"use client";

import { getPublicSiteSettingsAction } from "@/server/site-content/actions";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { SiteSettings } from "@/types/site";

export type { SiteSettings };

interface SiteSettingsContextValue {
  settings: SiteSettings;
  refresh: () => void;
}

const defaults: SiteSettings = {
  siteName: "SoalHub",
  logoUrl: null,
  faviconUrl: null,
  siteUrl: "https://soalhub.com",
};

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  settings: defaults,
  refresh: () => {},
});

export const useSiteSettings = () => {
  const ctx = useContext(SiteSettingsContext);
  return ctx.settings;
};

/**
 * Hydrated by server data when available: each zone layout fetches
 * /public/site-settings server-side (revalidate 30s, selaras cache backend)
 * and passes it as initialSettings → NO redundant client fetch on mount.
 * If initialSettings absent (defensive), falls back to a client fetch.
 */
export const SiteSettingsProvider = ({
  children,
  initialSettings,
}: {
  children: ReactNode;
  initialSettings?: SiteSettings | null;
}) => {
  const [settings, setSettings] = useState<SiteSettings>(initialSettings ?? defaults);

  const refresh = useCallback(async () => {
    // getSiteSettings = fetcher 'use cache' (tag site-settings) — Phase 3
    const r = await getPublicSiteSettingsAction();
    if (!r.ok) return;
    setSettings(r.data);
  }, []);

  // Only fetch on mount when NOT hydrated from server. Avoids a duplicate
  // request when the layout already passed initialSettings.
  useEffect(() => {
    if (!initialSettings) refresh();
  }, [initialSettings, refresh]);

  return (
    <SiteSettingsContext value={{ settings, refresh }}>{children}</SiteSettingsContext>
  );
};
